import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { fetchArtists, setLastUsedArtist } from '../api/artists'
import { getActiveArtistId, setActiveArtistId } from '../api/client'
import { useAuth } from './authContext'

type ArtistRecord = {
  id: string
  name: string
}

type ArtistScopeState = {
  artists: ArtistRecord[]
  selectedArtistId: string | null
  selectArtist: (artistId: string) => void
  isLoading: boolean
  error: string | null
  accountType: string | null
  isMultiArtist: boolean
}

const ArtistScopeContext = createContext<ArtistScopeState | undefined>(undefined)

function normaliseArtistId(value: string | null | undefined) {
  return value ? String(value) : null
}

export function ArtistProvider({ children }: { children: React.ReactNode }) {
  const { accessToken, isAuthenticated } = useAuth()
  const [artists, setArtists] = useState<ArtistRecord[]>([])
  const [selectedArtistId, setSelectedArtistId] = useState<string | null>(() => {
    return normaliseArtistId(getActiveArtistId())
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [accountType, setAccountType] = useState<string | null>(null)

  useEffect(() => {
    if (!selectedArtistId) return
    setActiveArtistId(selectedArtistId)
  }, [selectedArtistId])

  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      setArtists([])
      setSelectedArtistId(null)
      setError(null)
      return
    }

    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await fetchArtists()
        if (cancelled) return
        const list = Array.isArray(response?.artists) ? response.artists : []
        const responseAccountType =
          typeof response?.account_type === "string" ? response.account_type.toLowerCase() : null
        setArtists(list)
        setAccountType(responseAccountType)
        const current = normaliseArtistId(response?.current)
        setSelectedArtistId((prev) => {
          if (!prev && current) return current
          if (prev && !list.find((item: ArtistRecord) => item.id === prev)) {
            return normaliseArtistId(list[0]?.id ?? current ?? prev)
          }
          return prev
        })
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load artists')
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    load()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, isAuthenticated])

  const selectArtist = useCallback(
    (artistId: string) => {
      const normalized = normaliseArtistId(artistId)
      // Update the request header source synchronously so pages that refetch on
      // the switch never send the previous artist's X-Artist-Id.
      if (normalized) setActiveArtistId(normalized)
      setSelectedArtistId(normalized)
      if (normalized) {
        setLastUsedArtist(normalized).catch(() => {
          // Swallow errors so the UI remains responsive even if the preference update fails.
        })
      }
    },
    [],
  )

  const value = useMemo<ArtistScopeState>(
    () => ({
      artists,
      selectedArtistId,
      selectArtist,
      isLoading,
      error,
      accountType,
      isMultiArtist: (accountType ?? "").toLowerCase() === "multi_artist",
    }),
    [artists, selectedArtistId, selectArtist, isLoading, error, accountType],
  )

  return <ArtistScopeContext.Provider value={value}>{children}</ArtistScopeContext.Provider>
}

export function useArtistScope(): ArtistScopeState {
  const ctx = useContext(ArtistScopeContext)
  if (!ctx) {
    throw new Error('useArtistScope must be used within an ArtistProvider')
  }
  return ctx
}

export { ArtistScopeContext }

export function useArtistScopeSafe(): ArtistScopeState {
  const ctx = useContext(ArtistScopeContext)
  if (ctx) {
    return ctx
  }
  return {
    artists: [],
    selectedArtistId: null,
    selectArtist: () => {},
    isLoading: false,
    error: 'Artist scope unavailable',
    accountType: null,
    isMultiArtist: false,
  }
}
